/**
 * Free-form grade-level → canonical grade-range code matcher. Pure and
 * browser-safe — lives in constants so the Customers client can prefill
 * portion-band suggestions with EXACTLY the matcher the server uses.
 *
 * IMPORTANT: this is a setup-time SUGGESTION source only. The persisted
 * per-campus match (`LocationFactVocabulary.gradeRangeId`) is the runtime
 * source of truth; launched campuses never re-run this matcher.
 * `@ordo/schema` re-exports it from `managers/enrollment-audience`.
 */

/**
 * Resolve a free-form grade level ("K", "5", "Year 6", "Grade 3",
 * "PreK", …) to a canonical grade-range code ("pk3-5", "k-8", "9-12",
 * "infant", "age-1-2", "adult"), or `null` when it doesn't match any known
 * band. Every grade K through 8 maps to the ONE combined `k-8` band
 * (2026-09-15, Sheila: "Change all K-5 and 6-8 splits to K-8 universally");
 * the matcher never emits the retired `k-5` / `6-8` codes. Pure.
 */
export interface GradeRangeCodeForGradeLevelOptions {
  /**
   * Historical opt-in (pre-2026-09-15) for K-12 campuses to prefer the
   * combined K-8 tier over K-5 / 6-8. K-8 is now the ONLY result for grades
   * K..8, so the flag changes nothing; it is accepted so existing callers
   * keep compiling.
   */
  preferK8?: boolean
}

export function gradeRangeCodeForGradeLevel(
  gradeLevel: string | null,
  _options: GradeRangeCodeForGradeLevelOptions = {},
): string | null {
  if (!gradeLevel) {
    return null
  }
  const normalized = gradeLevel.trim().toLowerCase()
  if (!normalized) {
    return null
  }
  if (/\badult\b/u.test(normalized)) {
    return 'adult'
  }
  if (/\binfant\b/u.test(normalized)) {
    return 'infant'
  }
  if (/\b(age\s*)?[12]\b/u.test(normalized) && /\b(age|toddler)\b/u.test(normalized)) {
    return 'age-1-2'
  }
  // `pk[3-5]?` also covers the provisioning-seeded "PK3"/"PK4"/"PK5" grade
  // values, where the digit is glued to the letters (no word boundary).
  if (/\b(pre[-\s]?k|prek|pk[3-5]?|preschool|tk)\b/u.test(normalized)) {
    return 'pk3-5'
  }
  if (/\bk\s*[-–]\s*8\b/u.test(normalized) || /\bk\s*[-–]\s*12\b/u.test(normalized)) {
    return 'k-8'
  }
  if (/\b(k|kindergarten|grade\s*0)\b/u.test(normalized)) {
    return 'k-8'
  }
  const match = normalized.match(/\b(1[0-2]|[1-9])(?:st|nd|rd|th)?\b/u)
  const numeric = match ? Number(match[1]) : Number.NaN
  if (!Number.isFinite(numeric)) {
    return null
  }
  if (numeric >= 1 && numeric <= 8) {
    return 'k-8'
  }
  if (numeric >= 9 && numeric <= 12) {
    return '9-12'
  }
  return null
}

/**
 * True when any grade in the set belongs to the combined K-8 band. Kept for
 * call-site compatibility: before 2026-09-15 this decided whether a campus
 * opted into the K-8 tier over K-5 / 6-8; K-8 is now the only tier for
 * grades K..8, so the answer is simply "does this set contain a K-8 grade".
 */
export function shouldPreferK8PortionTierForGradeLevels(gradeLevels: ReadonlyArray<string | null>): boolean {
  return gradeLevels.some((level) => gradeRangeCodeForGradeLevel(level) === 'k-8')
}

/**
 * Superset of MENU_GRADE_RANGE_LABELS (menu-template-title.ts): menu titles
 * only ever see the four menu bands; these labels also cover the non-menu
 * ranges (infant / ages / adult) shown in provisioning surfaces. Keep the
 * shared codes' labels identical between the two maps.
 */
const GRADE_RANGE_CODE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  infant: 'Infant',
  'age-1-2': 'Age 1-2',
  'age-1-5': 'Age 1-5',
  'age-3-5': 'PK3-5',
  'pk3-5': 'PK3-5',
  pk: 'PK',
  'k-8': 'K-8',
  // Retired bands (portion-groups.ts) — legacy rows still label.
  'k-5': 'K-5',
  '6-8': '6-8',
  '9-12': '9-12',
  'k-12': 'K-12',
  adult: 'Adult',
})

export function formatGradeRangeCodeLabel(code: string | null | undefined): string | null {
  const normalized = code?.trim().toLowerCase().replace(/_/gu, '-')
  if (!normalized) {
    return null
  }
  return GRADE_RANGE_CODE_LABELS[normalized] ?? code?.trim() ?? null
}
